import { useRef, useState, useEffect } from 'react'
import {
    CalendarIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    EyeIcon,
    PencilIcon,
    TrashIcon,
    InformationCircleIcon,
    ArchiveBoxIcon,
    CubeIcon
} from '@heroicons/react/24/outline'
import { colorMap, formatDate } from '../../../Common'

const ServicosTable = ({ data, loading, title, headers, viewServico, editServico, deleteServico, color = 'blue', previous, next, page, isAdmin = false }) => {
    const colorClasses = colorMap[color] || colorMap.blue

    const tableScrollRef = useRef(null)
    const tableContainerRef = useRef(null)
    const [showLeftArrow, setShowLeftArrow] = useState(false)
    const [showRightArrow, setShowRightArrow] = useState(true)
    const [arrowTop, setArrowTop] = useState('50%')

    const scrollTable = (direction) => {
        if (tableScrollRef.current) {
            const scrollAmount = 300
            const newScrollLeft = tableScrollRef.current.scrollLeft + (direction === 'left' ? -scrollAmount : scrollAmount)
            tableScrollRef.current.scrollTo({
                left: newScrollLeft,
                behavior: 'smooth'
            })
        }
    }

    const checkScrollPosition = () => {
        if (tableScrollRef.current) {
            const { scrollLeft, scrollWidth, clientWidth } = tableScrollRef.current
            const isAtStart = scrollLeft <= 1
            const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 1

            setShowLeftArrow(!isAtStart)
            setShowRightArrow(!isAtEnd)
        }
    }

    const calculateArrowPosition = () => {
        if (tableContainerRef.current && tableScrollRef.current) {
            const containerRect = tableContainerRef.current.getBoundingClientRect()
            const scrollRect = tableScrollRef.current.getBoundingClientRect()
            const headerHeight = scrollRect.top - containerRect.top
            const tableHeight = scrollRect.height
            const topPosition = headerHeight + (tableHeight / 2)
            const containerHeight = containerRect.height
            const percentage = (topPosition / containerHeight) * 100
            setArrowTop(`${percentage}%`)
        }
    }

    useEffect(() => {
        const tableElement = tableScrollRef.current
        if (tableElement) {
            const timer = setTimeout(() => {
                checkScrollPosition()
                calculateArrowPosition()
            }, 1)

            tableElement.addEventListener('scroll', checkScrollPosition)
            window.addEventListener('resize', () => {
                checkScrollPosition()
                calculateArrowPosition()
            })

            return () => {
                clearTimeout(timer)
                tableElement.removeEventListener('scroll', checkScrollPosition)
                window.removeEventListener('resize', checkScrollPosition)
            }
        }
    }, [data, loading])

    return (
        <>
            <div className="relative" ref={tableContainerRef}>
                {showLeftArrow && (
                    <button
                        onClick={() => scrollTable('left')}
                        className={`absolute -left-14 -translate-y-1/2 z-30 p-2.5 rounded-full ${colorClasses.headerButtonBg} text-white shadow-lg hover:opacity-90 transition-all duration-200 hidden lg:flex items-center justify-center`}
                        title="Rolar para a esquerda"
                        style={{ top: arrowTop }}
                    >
                        <ChevronLeftIcon className="w-5 h-5" />
                    </button>
                )}

                {showRightArrow && (
                    <button
                        onClick={() => scrollTable('right')}
                        className={`absolute -right-14 -translate-y-1/2 z-30 p-2.5 rounded-full ${colorClasses.headerButtonBg} text-white shadow-lg hover:opacity-90 transition-all duration-200 hidden lg:flex items-center justify-center`}
                        title="Rolar para a direita"
                        style={{ top: arrowTop }}
                    >
                        <ChevronRightIcon className="w-5 h-5" />
                    </button>
                )}

                <div className="bg-white rounded-2xl shadow-lg overflow-hidden relative">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
                    </div>

                    <div className="relative">
                        <div
                            ref={tableScrollRef}
                            className="overflow-x-auto scrollbar-hide"
                            style={{
                                scrollbarWidth: 'none',
                                msOverflowStyle: 'none',
                                WebkitOverflowScrolling: 'touch'
                            }}
                            onScroll={checkScrollPosition}
                        >
                            <style>{`
                                    .scrollbar-hide::-webkit-scrollbar {
                                        display: none;
                                    }
                                `}</style>

                            {loading ? (
                                <div className="flex items-center justify-center py-12">
                                    <div className="text-center">
                                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-cyan mx-auto"></div>
                                        <p className="mt-4 text-gray-600">Carregando {title}...</p>
                                    </div>
                                </div>
                            ) : data && data.length > 0 ? (
                                <>
                                    <div className="hidden lg:block px-3 py-2 bg-gray-50 border-b border-gray-200">
                                        <div className="grid grid-cols-5 gap-2 items-center text-xs font-medium text-gray-500 uppercase tracking-wide">
                                            {headers.map((header, index) => (
                                                <div key={header.key || index} className="flex items-center justify-center">
                                                    <header.icon className="w-3 h-3 mr-1" />
                                                    {header.label}
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="divide-y divide-gray-200">
                                        {data.map((item) => {
                                            return (
                                                <div
                                                    key={item.id}
                                                    className="px-3 py-2 hover:bg-gray-50 transition-colors duration-200"
                                                >
                                                    <div className="hidden lg:block">
                                                        <div className="grid grid-cols-5 gap-2 items-center">
                                                            <div className="flex items-center justify-center space-x-1">
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation()
                                                                        viewServico(item)
                                                                    }}
                                                                    className={`p-1.5 ${colorClasses.headerIconBg} ${colorClasses.headerIconText} rounded-lg hover:opacity-80 transition-all duration-200 transform hover:scale-110`}
                                                                    title="Visualizar detalhes"
                                                                >
                                                                    <EyeIcon className="w-4 h-4" />
                                                                </button>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation()
                                                                        editServico(item)
                                                                    }}
                                                                    className="p-1.5 bg-brand-cyan/20 text-brand-cyan-dark rounded-lg hover:bg-brand-cyan/30 transition-all duration-200 transform hover:scale-110"
                                                                    title="Editar serviço"
                                                                >
                                                                    <PencilIcon className="w-4 h-4" />
                                                                </button>
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation()
                                                                        deleteServico(item)
                                                                    }}
                                                                    className="p-1.5 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-all duration-200 transform hover:scale-110"
                                                                    title="Excluir serviço"
                                                                >
                                                                    <TrashIcon className="w-4 h-4" />
                                                                </button>
                                                            </div>

                                                            <div className="text-center">
                                                                <div className="text-xs font-medium text-gray-900 truncate" title={item.nome}>
                                                                    {item.nome}
                                                                </div>
                                                            </div>

                                                            <div className="flex justify-center">
                                                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${item.municipal === 1 ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}`}>
                                                                    {item.municipal === 1 ? 'Sim' : 'Não'}
                                                                </span>
                                                            </div>

                                                            <div className="text-center">
                                                                <div className="text-xs font-medium text-gray-900 truncate" title={item.codigo_servico}>
                                                                    {item.codigo_servico}
                                                                </div>
                                                            </div>

                                                            <div className="text-center">
                                                                <div className="text-xs font-medium text-gray-900">
                                                                    {formatDate(item.data_criacao)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>

                                                    <div className="lg:hidden">
                                                        <div className="space-y-3">
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex-1 min-w-0">
                                                                    <h3 className="text-sm font-semibold text-gray-900 truncate">{item.nome}</h3>
                                                                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">{item.descricao}</p>
                                                                </div>
                                                                <div className="flex space-x-2 ml-2">
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation()
                                                                            viewServico(item)
                                                                        }}
                                                                        className={`p-2 ${colorClasses.headerIconBg} ${colorClasses.headerIconText} rounded-lg hover:opacity-80 transition-all duration-200`}
                                                                        title="Visualizar detalhes"
                                                                    >
                                                                        <EyeIcon className="w-4 h-4" />
                                                                    </button>
                                                                    {isAdmin && (
                                                                        <>
                                                                            <button
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation()
                                                                                    editServico(item)
                                                                                }}
                                                                                className="p-2 bg-brand-cyan/20 text-brand-cyan-dark rounded-lg hover:bg-brand-cyan/30 transition-all duration-200"
                                                                                title="Editar serviço"
                                                                            >
                                                                                <PencilIcon className="w-4 h-4" />
                                                                            </button>
                                                                            <button
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation()
                                                                                    deleteServico(item)
                                                                                }}
                                                                                className="p-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-all duration-200"
                                                                                title="Excluir serviço"
                                                                            >
                                                                                <TrashIcon className="w-4 h-4" />
                                                                            </button>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            <div className="space-y-2 pl-2">
                                                                <div className="flex items-start">
                                                                    <CubeIcon className="w-4 h-4 text-gray-400 mt-0.5 mr-2 flex-shrink-0" />
                                                                    <div className="flex-1 min-w-0">
                                                                        <span className="text-xs font-medium text-gray-500">Código:</span>
                                                                        <span className="text-xs text-gray-900 ml-2">{item.codigo_servico}</span>
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-start">
                                                                    <InformationCircleIcon className="w-4 h-4 text-gray-400 mt-0.5 mr-2 flex-shrink-0" />
                                                                    <div className="flex-1 min-w-0">
                                                                        <span className="text-xs font-medium text-gray-500">Municipal:</span>
                                                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ml-2 ${item.municipal === 'true' ? 'bg-purple-100 text-purple-800' : 'bg-gray-100 text-gray-800'}`}>
                                                                            {item.municipal === 'true' ? 'Sim' : 'Não'}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center">
                                                                    <CalendarIcon className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                                                                    <div className="flex-1 min-w-0">
                                                                        <span className="text-xs font-medium text-gray-500">Criação:</span>
                                                                        <span className="text-xs text-gray-900 ml-2">
                                                                            {formatDate(item.data_criacao)}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </>
                            ) : (
                                <div className="px-6 py-8 text-center text-gray-500">
                                    <ArchiveBoxIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                    <h3 className="text-lg font-medium text-gray-900 mb-2">Nenhum {title} encontrado</h3>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {data && data.length > 0 && (
                <div className="flex items-center justify-between bg-white rounded-2xl shadow-lg border border-gray-200 p-4 mt-6">
                    <div className="flex space-x-2">
                        <button
                            onClick={previous}
                            disabled={page === 0}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                        >
                            <ChevronLeftIcon className="w-4 h-4" />
                        </button>
                        <span className="px-3 py-2 text-sm font-medium text-gray-700">
                            Página {page + 1}
                        </span>
                        <button
                            onClick={next}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                        >
                            <ChevronRightIcon className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            )}
        </>
    )
}

export default ServicosTable

